import { Link, type LinkProps } from '@tanstack/react-router'
import type { Task } from '@/entities/task'
import { cn } from '@/shared/lib'
import { Checkbox } from '@/shared/ui/checkbox'
import { Chip } from '@/shared/ui/chip'
import { DateStamp } from '@/shared/ui/date-stamp'
import { Icon } from '@/shared/ui/icon'

interface TaskRowProps {
  task: Task
  isEmployee: boolean
  canToggle: boolean
  onToggle: (done: boolean) => void
  onEdit?: () => void
  /** Ссылка на страницу задачи; без неё тайтл — статичный текст. */
  taskLink?: (taskId: string) => LinkProps
}

// Строка задачи: чекбокс, название, срок.
export function TaskRow({ task, isEmployee, canToggle, onToggle, onEdit, taskLink }: TaskRowProps) {
  return (
    <li className="flex items-center gap-3 rounded-control bg-bg-surface px-4 py-3 ring-1 ring-border-subtle ring-inset">
      <Checkbox
        checked={task.done}
        disabled={!canToggle}
        aria-label={task.done ? `Снять «${task.title}»` : `Выполнить «${task.title}»`}
        onChange={(e) => onToggle(e.target.checked)}
      />
      <div className="min-w-0 flex-1">
        {taskLink ? (
          <Link
            {...taskLink(task.id)}
            className={cn(
              'block truncate text-sm transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
              task.done ? 'text-text-tertiary line-through' : 'text-text-primary',
            )}
          >
            {task.title}
          </Link>
        ) : (
          <span
            className={cn(
              'block truncate text-sm',
              task.done ? 'text-text-tertiary line-through' : 'text-text-primary',
            )}
          >
            {task.title}
          </span>
        )}
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-tertiary">
          <span>
            создана <DateStamp value={task.created_at} />
          </span>
          {task.due && !task.done && (
            <span>
              срок <DateStamp value={task.due} deadline />
            </span>
          )}
          {task.done && task.completed_at && (
            <span>
              выполнено <DateStamp value={task.completed_at} />
            </span>
          )}
        </span>
      </div>
      {isEmployee && task.blocking && !task.done && (
        <Chip tone="accent" variant="outline">
          блокирует
        </Chip>
      )}
      {onEdit && (
        <button
          type="button"
          aria-label={`Изменить «${task.title}»`}
          onClick={onEdit}
          className="shrink-0 rounded-control p-1.5 text-text-tertiary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
        >
          <Icon name="pencil" size={16} />
        </button>
      )}
    </li>
  )
}
